import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Save, Shield } from 'lucide-react';

const ROLES = [
  { v: 'super_admin', l: 'Super Admin' },
  { v: 'hr_admin', l: 'HR Admin' },
  { v: 'payroll_admin', l: 'Payroll Admin' },
  { v: 'manager', l: 'Manager' },
  { v: 'employee', l: 'Employee' },
];

const MODULES = [
  ['dashboard', 'Dashboard'],
  ['employees', 'Employees'],
  ['onboarding', 'Onboarding'],
  ['documents', 'Documents'],
  ['attendance', 'Attendance'],
  ['leaves', 'Leaves'],
  ['expenses', 'Expenses'],
  ['payroll', 'Payroll'],
  ['salary_config', 'Salary Config'],
  ['reminders', 'Reminders'],
  ['companies', 'Companies'],
  ['calendar', 'Calendar'],
  ['user_admin', 'User Admin'],
  ['audit_logs', 'Audit Logs'],
  ['settings', 'Settings'],
];

const PERMISSIONS = ['view', 'create', 'edit', 'delete', 'approve', 'reject', 'export', 'upload', 'download', 'process'];

const RolePermissions = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/role-permissions');
      setData(res.data.permissions || {});
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const togglePerm = (role, module, perm) => {
    const next = JSON.parse(JSON.stringify(data));
    if (!next[role]) next[role] = {};
    if (!next[role][module]) next[role][module] = [];
    const idx = next[role][module].indexOf(perm);
    if (idx === -1) next[role][module].push(perm);
    else next[role][module].splice(idx, 1);
    setData(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/role-permissions', { permissions: data });
      toast.success('Permissions saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="role-permissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Shield />Role Permissions Matrix</h1>
          <p className="text-muted-foreground">Configure module-level access for each role</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-permissions-btn">
          {saving && <Loader2 size={14} className="animate-spin mr-1" />}<Save size={16} className="mr-2" />Save Matrix
        </Button>
      </div>

      <p className="text-xs text-muted-foreground bg-muted/30 p-3 rounded">
        ℹ This matrix is informational — the live API enforces role checks at the endpoint level. Editing this matrix records intent for governance and will progressively drive UI access control in upcoming releases.
      </p>

      {ROLES.map(role => (
        <Card key={role.v}>
          <CardHeader>
            <CardTitle className="text-lg">{role.l}</CardTitle>
            <CardDescription>Module access for {role.l}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium">Module</th>
                    {PERMISSIONS.map(p => <th key={p} className="text-center px-2 py-2 font-medium capitalize">{p}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map(([mv, ml]) => {
                    const granted = (data[role.v] && data[role.v][mv]) || [];
                    return (
                      <tr key={mv} className="border-b last:border-0">
                        <td className="py-2 px-3 font-medium">{ml}</td>
                        {PERMISSIONS.map(p => (
                          <td key={p} className="text-center px-2 py-2">
                            <input
                              type="checkbox"
                              checked={granted.includes(p)}
                              onChange={() => togglePerm(role.v, mv, p)}
                              className="h-4 w-4"
                              data-testid={`perm-${role.v}-${mv}-${p}`}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default RolePermissions;
